import { ArchiveOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  MenuProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import Modal from "../../App/Components/Modal/Modal";
import { useClientCustomFieldData } from "../../App/Queries/ClientQueries";
import { ICampaignFormState } from "../../App/Typings/Campaigns";
import { Rule } from "../../App/Typings/DynamicGroupRules";
import { IGroup } from "../../App/Typings/Groups";
import { FieldType } from "../DynamicGroup";
import { DynamicFilterRuleValue } from "./DynamicFilterRuleValue";

interface DynamicFilterRulesProps {
  rules: Rule[];
  onChange: (rules: Rule[]) => void;
  fieldOptions: FieldType[];
  helpTitle?: string;
  helpId?: string;
  campaigns?: ICampaignFormState[];
  selectedCampaign?: string;
  isCampaign?: boolean;
  FieldHelpContent?: JSX.Element;
  CommandHelpContent?: JSX.Element;
  tags?: string[];
  groups: IGroup[];
  archivedFieldIds?: string[];
  showArchivedFieldError?: boolean;
}

export function DynamicFilterRules({
  rules,
  onChange,
  fieldOptions,
  helpTitle,
  helpId,
  campaigns,
  selectedCampaign,
  isCampaign = false,
  FieldHelpContent,
  CommandHelpContent,
  tags,
  groups,
  archivedFieldIds = [],
  showArchivedFieldError = false,
}: DynamicFilterRulesProps) {
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<JSX.Element>(<></>);

  const isCampaignSelected = !!selectedCampaign && selectedCampaign.toLocaleLowerCase() !== "none";

  const availableCandidateFields = isCampaign
    ? []
    : Array.from(candidateFields.keys()).filter(
        (field) => !isCampaignSelected || field !== "campaignResponses.campaignId",
      );

  const availableCampaignFields = fieldOptions.filter((f) => !isFieldExcluded(f));

  const { data: customFieldData } = useClientCustomFieldData();

  const addRule = (id: string, rules: Rule[], newRule: Rule, and: boolean) => {
    rules.forEach((r) => {
      if (r.id === id) {
        if (!r.rules) r.rules = [];
        and ? r.rules.push(newRule) : rules.push(newRule);
      } else if (r.rules && r.rules.length > 0) {
        addRule(id, r.rules, newRule, and);
      }
    });
  };

  const createRule = (id: string, add: boolean) => {
    const newRules = [...rules];

    addRule(
      id,
      newRules,
      {
        id: generateId(32),
        filter: {
          command: "",
          field: "",
          type: "",
          value: "",
        },
      } as Rule,
      add,
    );

    onChange(newRules);
  };

  const removeRuleFromArray = (id: string, rules: Rule[]) => {
    const index = rules ? rules.findIndex((r) => r.id === id) : -1;
    if (index >= 0) {
      rules.splice(index, 1);
    } else {
      rules.forEach((r) => {
        if (r.rules && r.rules.length > 0) {
          removeRuleFromArray(id, r.rules);
        }
      });
    }
  };

  const removeRule = (id: string) => {
    const newRules = [...rules];

    removeRuleFromArray(id, newRules);

    onChange(newRules);
  };

  const updateRuleValue = (id: string, field: string, value: any) => {
    const newRules = [...rules];
    updateRule(id, newRules, field, value);
    onChange(newRules);
  };

  const openModal = (type: string) => {
    if (type === "field" && FieldHelpContent) {
      setModalContent(FieldHelpContent);
    } else if (type === "command" && CommandHelpContent) {
      setModalContent(CommandHelpContent);
    } else {
      setModalContent(<></>);
      return;
    }
    setIsHelpOpen(true);
  };

  const getFieldTypeById = (id: string): string => {
    const type = fieldOptions.find((c) => c.id === id)?.field.fieldType;

    if (type === "dropdown" || type === "multiSelect") {
      return "array";
    } else if (type === "upload") {
      return type;
    }

    return fieldTypes.get(id) ?? "string";
  };

  const renderRules = (rules: Rule[], current: number) => {
    return rules
      ? rules.map((r: Rule, ruleIndex: number) => {
          const isFirstItem = r === rules.at(0) && 0 === current;
          return (
            <React.Fragment key={`grid-row-${ruleIndex}-${current}`}>
              {!isFirstItem && (
                <Grid item xs={12}>
                  <Divider light style={{ marginBottom: "10px" }} />
                </Grid>
              )}
              <Grid item xs={1} style={{ paddingTop: "25px" }}>
                {isFirstItem ? "" : <SubdirectoryArrowRightIcon />}
              </Grid>
              <Grid item xs={3}>
                <FieldNameSelect
                  disabled={!fieldOptions || fieldOptions.length <= 0}
                  rule={r}
                  ruleIndex={ruleIndex}
                  availableCandidateFields={availableCandidateFields}
                  availableCampaignFields={availableCampaignFields}
                  openModal={openModal}
                  archivedFieldIds={archivedFieldIds}
                  showArchivedFieldError={showArchivedFieldError}
                  onChange={(e) => {
                    updateRuleValue(r.id, "field", e.target.value);
                    updateRuleValue(r.id, "type", getFieldTypeById(e.target.value));
                    updateRuleValue(r.id, "value", "");
                    updateRuleValue(r.id, "command", "");
                  }}
                />
              </Grid>
              <HiddenDataTypeFields r={r} ruleIndex={ruleIndex} />
              <Grid item xs={3}>
                <CommandSelect
                  rule={r}
                  ruleIndex={ruleIndex}
                  openModal={openModal}
                  onChange={(e) => updateRuleValue(r.id, "command", e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <DynamicFilterRuleValue
                  rule={r}
                  groups={groups}
                  campaigns={campaigns}
                  fieldOptions={fieldOptions}
                  updateRuleValue={updateRuleValue}
                  index={ruleIndex}
                  key={ruleIndex}
                  tags={tags}
                  customFieldData={customFieldData}
                />
              </Grid>
              <Grid item xs={2} style={{ textAlign: "right" }}>
                <AddOrRemoveRules
                  r={r}
                  ruleIndex={ruleIndex}
                  createRule={createRule}
                  removeRule={removeRule}
                  current={current}
                  rules={rules}
                />
              </Grid>
              {r.rules && r.rules.length > 0 && (
                <Grid item xs={12} data-test={`and-rule-group-${ruleIndex}`}>
                  <Grid container spacing={1}>
                    <Grid item xs={1}></Grid>
                    <Grid item xs={11}>
                      <Grid container spacing={1}>
                        {renderRules(r.rules, current + 1)}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </React.Fragment>
          );
        })
      : Array(<></>);
  };

  return (
    <>
      {renderRules(rules, 0)}
      <Modal
        title={helpTitle ?? "Help"}
        footerContent={<></>}
        dataTestId={helpId ?? "help-modal"}
        isOpen={isHelpOpen}
        handleCloseModal={() => setIsHelpOpen(false)}
        content={modalContent}
        sx={{
          ".MuiTypography-body2": {
            margin: "10px",
          },
        }}
      />
    </>
  );
}

const FieldNameSelect = ({
  disabled,
  rule,
  ruleIndex,
  onChange,
  availableCandidateFields,
  availableCampaignFields,
  openModal,
  archivedFieldIds = [],
  showArchivedFieldError = false,
}: {
  disabled: boolean;
  rule: Rule;
  ruleIndex: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  availableCandidateFields: string[];
  availableCampaignFields: FieldType[];
  openModal: (type: string) => void;
  archivedFieldIds?: string[];
  showArchivedFieldError?: boolean;
}) => {
  const isArchivedFieldSelected = showArchivedFieldError && archivedFieldIds.includes(rule.filter.field);

  return (
    <FormControl sx={{ width: "90%" }} disabled={disabled}>
      <FormLabel
        htmlFor={`field-name-${ruleIndex}`}
        id={`field-name-${ruleIndex}-label`}
        error={isArchivedFieldSelected}
      >
        Field Name
        <IconButton aria-label="help" size="small" onClick={() => openModal("field")}>
          <HelpOutlineIcon fontSize="inherit" />
        </IconButton>
      </FormLabel>
      <TextField
        data-test={`select-field-name-${ruleIndex}`}
        id={`field-name-${ruleIndex}`}
        value={rule.filter.field}
        select
        error={isArchivedFieldSelected}
        SelectProps={{
          MenuProps: { "data-test": `field-name-menu-${ruleIndex}` } as Partial<MenuProps>,
        }}
        sx={{
          "& .MuiInputBase-input": {
            display: "flex",
            gap: 1,
          },
        }}
        onChange={onChange}
      >
        {availableCandidateFields.length > 0 && <ListSubheader>Candidate Information</ListSubheader>}
        {availableCandidateFields.map((k, index) => (
          <MenuItem
            value={k}
            data-test={`field-candidate-${ruleIndex}-${index}`}
            key={`field-${ruleIndex}-9999${index}`}
            style={{ marginLeft: "10px", marginRight: "10px" }}
          >
            {candidateFields.get(k)}
          </MenuItem>
        ))}
        {availableCampaignFields.length > 0 && <ListSubheader>Form Responses</ListSubheader>}
        {availableCampaignFields.map((o, index) => {
          const historicLabels = new Set(o.field.labelHistory || []);
          historicLabels.delete(o.field.fieldName);
          return (
            <MenuItem
              value={o.id}
              data-test={`field-campaign-${ruleIndex}-${index}`}
              key={`field-${ruleIndex}-${index}`}
              sx={{
                marginLeft: "10px",
                marginRight: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span>{o.field.fieldName}</span>
                {o.field.archived && (
                  <Tooltip title="Archived Field" placement="right" arrow>
                    <ArchiveOutlined fontSize="small" color="info" />
                  </Tooltip>
                )}
              </Box>
              {historicLabels.size > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 1 }}>
                        Historical Label Variants:
                      </Typography>
                      {Array.from(historicLabels)
                        .sort()
                        .map((label) => (
                          <Typography key={label} variant="caption" sx={{ display: "block" }}>
                            • {label}
                          </Typography>
                        ))}
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontStyle: "italic",
                      cursor: "help",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  >
                    (+{historicLabels.size} label variant{historicLabels.size !== 1 ? "s" : ""})
                  </Typography>
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </TextField>
    </FormControl>
  );
};

const HiddenDataTypeFields = ({ r, ruleIndex }: { r: Rule; ruleIndex: number }) => (
  <div style={{ display: "none" }}>
    <TextField data-test="data-types" select value={r.filter.type} label="Data Type">
      {[...types.keys()]?.map((t, typeIndex) => (
        <MenuItem value={t} key={`type-${ruleIndex}-${typeIndex}`}>
          {t}
        </MenuItem>
      ))}
    </TextField>
  </div>
);

const CommandSelect = ({
  rule,
  ruleIndex,
  onChange,
  openModal,
}: {
  rule: Rule;
  ruleIndex: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  openModal: (type: string) => void;
}) => {
  return (
    <FormControl sx={{ width: "90%" }}>
      <FormLabel
        error={!rule.filter.command}
        htmlFor={`command-field-${ruleIndex}`}
        id={`command-field-${ruleIndex}-label`}
      >
        Command
        <IconButton aria-label="help" size="small" onClick={() => openModal("command")}>
          <HelpOutlineIcon fontSize="inherit" />
        </IconButton>
      </FormLabel>
      <TextField
        data-test={`select-command-${ruleIndex}`}
        id={`command-field-${ruleIndex}`}
        value={rule.filter.command}
        select
        SelectProps={{ MenuProps: { "data-test": `field-command-menu-${ruleIndex}` } } as any}
        error={!rule.filter.command}
        onChange={onChange}
      >
        {(types.get(rule.filter.type) as string[])
          ? (types.get(rule.filter.type) as string[])?.map((c, commandIndex) => (
              <MenuItem value={c} key={`command-${ruleIndex}-${commandIndex}`}>
                {changeLabels(c, rule.filter.type)}
              </MenuItem>
            ))
          : []}
      </TextField>
    </FormControl>
  );
};

const AddOrRemoveRules = ({
  r,
  ruleIndex,
  createRule,
  removeRule,
  current,
  rules,
}: {
  r: Rule;
  ruleIndex: number;
  createRule: (id: string, add: boolean) => void;
  removeRule: (id: string) => void;
  current: number;
  rules: Rule[];
}) => {
  return (
    <>
      <Button
        data-test={`add-and-rule-${ruleIndex}`}
        onClick={() => createRule(r.id, true)}
        size="small"
        color="primary"
        disabled={current >= 3}
      >
        AND
      </Button>{" "}
      |
      <Button
        data-test={`add-or-rule-${ruleIndex}`}
        onClick={() => createRule(r.id, false)}
        size="small"
        color="primary"
        disabled={current >= 3}
      >
        OR
      </Button>
      <IconButton
        data-test={`remove-rule-${ruleIndex}`}
        onClick={() => removeRule(r.id)}
        color="error"
        aria-label="Remove rule"
        disabled={ruleIndex === 0 && current === 0 && rules.length <= 1}
      >
        <DeleteIcon />
      </IconButton>
    </>
  );
};

export const generateId = (length: number) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export const labels = new Map<string, string>([
  ["contains", "includes"],
  ["not contains", "does not include"],
  ["equals", "is"],
  ["not equals", "is not"],
]);

const changeLabels = (label: string, type: string) => {
  if (labels.has(label)) {
    return labels.get(label);
  }
  if (type === "datetime") {
    return dateCommandTranslateMap.get(label) ?? label;
  }
  return label;
};

const updateRule = (id: string, rules: Rule[], field: string, value: any) => {
  rules.forEach((r) => {
    if (r.id === id) {
      if (field === "command") {
        r.filter.command = value;
      }
      if (field === "field") {
        r.filter.field = value;
      }
      if (field === "type") {
        r.filter.type = value;
      }
      if (field === "value") {
        r.filter.value =
          typeof value === "object"
            ? { ...value, type: r.filter.type, value: value.value }
            : {
                type: r.filter.type,
                value: value,
              };
      }
    } else if (r.rules && r.rules.length > 0) {
      updateRule(id, r.rules, field, value);
    }
  });
};

const types = new Map([
  [
    "string",
    [
      "contains",
      "not contains",
      "not starts with",
      "starts with",
      "ends with",
      "not ends with",
      "equals",
      "not equals",
    ],
  ],
  ["location", ["greater than", "greater than or equal", "less than", "less than or equal"]],
  [
    "datetime",
    [
      "equals",
      "not equals",
      "greater than",
      "greater than or equal",
      "less than",
      "less than or equal",
      "between",
    ],
  ],
  ["array", ["contains", "not contains"]],
  ["interestpicker", ["contains", "not contains"]],
  ["boolean", ["equals", "not equals"]],
  ["upload", ["equals", "not equals"]],
]);
export const dateCommandTranslateMap = new Map([
  ["is", "is"],
  ["is not", "is not"],
  ["greater than", "after"],
  ["greater than or equal", "after or on"],
  ["less than", "before"],
  ["less than or equal", "before or on"],
  ["between", "between"],
]);

const fieldTypes = new Map([
  ["campaignResponses.campaignId", "array"],
  ["groupIds", "array"],
  ["createdDate", "datetime"],
  ["geolocation", "location"],
  ["campaignResponses.campaignResponses.optIn", "boolean"],
  ["campaignResponses.campaignResponses.interestPicker", "interestpicker"],
  ["campaignResponses.CandidateResponse.dateAdded", "datetime"],
  ["campaignResponses.CandidateResponse.optIn", "boolean"],
  ["campaignResponses.CandidateResponse.interestPicker", "interestpicker"],
  ["campaignResponses.CandidateResponse.location", "location"],
  ["tags", "array"],
  ["statusColor", "array"],
]);

const candidateFields = new Map([
  ["campaignResponses.campaignId", "Form"],
  ["groupIds", "Group"],
  ["firstName", "First Name"],
  ["lastName", "Last Name"],
  ["emailAddresses.emailAddress", "Email Address"],
  ["phoneNumbers.phoneNumber", "Phone Number"],
  ["createdDate", "Date Added"],
  ["geolocation", "Geolocation"],
  ["tags", "Tags"],
  ["statusColor", "Status"],
]);

const excludedCampaignFields = [
  "campaignResponses.campaignResponses.firstName",
  "campaignResponses.campaignResponses.lastName",
  "campaignResponses.campaignResponses.dateAdded",
];

export const isFieldExcluded = (type: FieldType) => {
  if (type.id === "campaignResponses.campaignResponses.location") {
    return type.field.fieldType !== "dropdown";
  }

  return excludedCampaignFields.includes(type.id);
};
